/**
 * How many filters are set: ONE count for the filter bar's badge, the
 * list's reset button, the filter sheet's title and its reset button,
 * and the map's filter pill. They used to count three different things
 * (URL keys, draft groups, written params) and read 2, 3 and 4 for the
 * same search (UI critique, 2026-10-18).
 *
 * A filter is a facet, not a key: a price range (min and max) is one
 * filter, so is a floor range, a listed-since window, a radius (centre
 * + km) and a mortgage kind with its terms. Pagination, sort, view and
 * layer keys are not filters; neither is `category` (a landing page
 * pins it with no control) nor `seller` (a context banner, not a chip).
 * The sale/rent switch counts only when it is not the default sale.
 */
import type { ReadonlyURLSearchParams } from "next/navigation";

const FACETS: readonly (readonly string[])[] = [
  ["leased"], ["viewed"], ["source"], ["building_type"], ["listing_kind"], ["property_kind"], ["rent_period"],
  ["price_min", "price_max"], ["rooms"], ["area_min", "area_max"], ["sot_min", "sot_max"], ["pps_min", "pps_max"],
  ["ppm_min", "ppm_max"],
  ["floor_min", "floor_max"], ["exclude_first_floor"], ["exclude_last_floor"], ["total_floors_min", "total_floors_max"],
  ["has_repair"], ["photo_condition"], ["photo_has"], ["tags"], ["has_mortgage"],
  ["mortgage_kind", "max_initial", "mortgage_rate_min", "mortgage_rate_max", "mortgage_terms_known", "mortgage_years"],
  ["listed_after", "listed_before"], ["has_bill_of_sale"], ["deed_gap"], ["seller_type"],
  ["location_ids", "complex_slug", "city"], ["deal_tier"], ["developer"], ["street"], ["metro_walk"],
  ["center_lat", "center_lng", "radius_km"], ["q"], ["draw"],
];
// Each fact read from the text is its own filter, like each amenity
// would be if they were not one facet: they sit in different topics.
const PER_VALUE = new Set(["fact"]);
// What the search rules out: the places as one filter, like the places
// picked, and the amenities as another.
const RULED_OUT: readonly (readonly string[])[] = [["exclude_location_ids", "exclude_city"], ["exclude_tags"]];

type ParamValue = string | string[] | null | undefined;
type Params = URLSearchParams | ReadonlyURLSearchParams | Record<string, ParamValue>;

function isSet(params: Params, key: string): boolean {
  if (typeof (params as URLSearchParams).getAll === "function") {
    return (params as URLSearchParams).getAll(key).some((v) => v !== "");
  }
  const v = (params as Record<string, ParamValue>)[key];
  return Array.isArray(v) ? v.length > 0 : v != null && v !== "";
}

function value(params: Params, key: string): string | null {
  if (typeof (params as URLSearchParams).get === "function") return (params as URLSearchParams).get(key);
  const v = (params as Record<string, ParamValue>)[key];
  return Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
}

function values(params: Params, key: string): string[] {
  if (typeof (params as URLSearchParams).getAll === "function") {
    return (params as URLSearchParams).getAll(key).filter((v) => v !== "");
  }
  const v = (params as Record<string, ParamValue>)[key];
  return (Array.isArray(v) ? v : v != null ? [v] : []).filter((x) => x !== "");
}

/** The number of filters set in a URL query or a written param object. */
export function countActiveFilters(params: Params): number {
  let n = 0;
  for (const key of PER_VALUE) n += values(params, key).length;
  for (const facet of [...FACETS, ...RULED_OUT]) {
    if (!facet.some((k) => isSet(params, k))) continue;
    // Sale is the default: "leased=false" is not a filter.
    if (facet[0] === "leased" && value(params, "leased") === "false") continue;
    n += 1;
  }
  return n;
}
